import { api } from './api';
import type { TermDate } from '../components/TermDates';

export type PriorityArea = { text: string; suggestion: string | null };

export interface SchoolProfile {
  /** Absent on older APIs (treated as organisation). */
  type?: 'organisation' | 'personal';
  phase: string | null;
  phaseOther?: string | null;
  country: string | null;
  staffBand: string | null;
  trustName: string | null;
  /** Personal accounts: where they work, if they said. */
  workplace?: string | null;
  priorities: string[];
  priorityAreas?: PriorityArea[];
  /** The organisation's terms, earliest first (absent on older APIs). */
  termDates?: TermDate[];
  defaultFrameworkId?: string | null;
  onboardedAt: string | null;
  termsAcceptedAt: string | null;
  options: {
    phases: string[];
    educationSettings?: { value: string; label: string }[];
    countries: string[];
    allCountries?: string[];
    staffBands: string[];
    priorities: string[];
    focusSuggestions?: { all: string[]; fe: string[]; careers: string[] };
    settingGroups?: Record<string, 'school' | 'fe' | 'careers' | 'all'>;
  };
}

/** Suggested focus areas for a setting (follows an unsaved change of setting). */
export function suggestionsFor(profile: SchoolProfile | undefined, setting: string | null): string[] {
  const o = profile?.options;
  if (!o?.focusSuggestions) return o?.priorities ?? [];
  const group = (setting && o.settingGroups?.[setting]) || 'all';
  return [...o.focusSuggestions.all, ...(group === 'fe' ? o.focusSuggestions.fe : group === 'careers' ? o.focusSuggestions.careers : [])];
}

/** Areas as stored, or built from older plain-text priorities. */
export function areasOf(profile: SchoolProfile | undefined): PriorityArea[] {
  if (!profile) return [];
  return profile.priorityAreas ?? profile.priorities.map((p) => ({ text: p, suggestion: p }));
}

export function settingOptions(profile: SchoolProfile | undefined) {
  const o = profile?.options;
  return o?.educationSettings ?? (o?.phases ?? []).map((v) => ({ value: v, label: PHASE_LABEL[v] ?? v }));
}

export interface FrameworkOption {
  id: string;
  name: string;
  description: string | null;
  status?: string;
}

export const getProfile = () => api<SchoolProfile>('/schools/mine/profile');
export const saveProfile = (
  body: Partial<Pick<SchoolProfile, 'phase' | 'phaseOther' | 'country' | 'staffBand' | 'trustName' | 'workplace'>> & {
    name?: string;
    priorities?: PriorityArea[] | string[];
    termDates?: TermDate[];
  },
) =>
  api<SchoolProfile>('/schools/mine/profile', { method: 'PUT', body: JSON.stringify(body) });
export const saveJobTitle = (jobTitle: string) => api('/users/me', { method: 'PUT', body: JSON.stringify({ jobTitle }) });
export const getFrameworks = async () =>
  (await api<{ data: FrameworkOption[] }>('/frameworks')).data.filter((f) => !f.status || f.status === 'approved');
export const setDefaultFramework = (frameworkId: string) =>
  api('/schools/mine/default-framework', { method: 'PUT', body: JSON.stringify({ frameworkId }) });
export const completeOnboarding = () =>
  api<SchoolProfile>('/schools/mine/onboarding/complete', { method: 'POST', body: JSON.stringify({ acceptTerms: true }) });

export const PHASE_LABEL: Record<string, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  all_through: 'All-through',
  further_education: 'Further education',
  special: 'Special school',
  other: 'Other',
};

/**
 * Roles come from the API so the web and the app share one list; this copy
 * is only used while that loads (or against an older API).
 */
export const JOB_TITLES = [
  'Class teacher',
  'Subject lead or head of department',
  'Head of year or pastoral lead',
  'SENCo',
  'Teaching assistant',
  'CPD or professional development lead',
  'Assistant headteacher',
  'Deputy headteacher',
  'Headteacher or principal',
  'Trust or group leader',
];
export const getRoleOptions = async () =>
  (await api<{ options: { jobRoles: string[] } }>('/users/me/teaching-profile')).options.jobRoles.filter((r) => r !== 'Other');
